import { useEffect, useRef, useState } from "react";
import { bundleDeadline } from "../helpers/bundleOfferPresentation";

/** Display-only clock. The owner refreshes eligibility/window/counts from the server. */
export function useBundleOfferCountdown(
    isOpen: boolean,
    expiresAt: string | null | undefined,
    onCountdownEnd?: () => void,
) {
    const deadline = bundleDeadline(expiresAt);
    const [now, setNow] = useState(Date.now);
    const onEnd = useRef(onCountdownEnd);
    onEnd.current = onCountdownEnd;
    const notifiedDeadline = useRef<number | null>(null);

    useEffect(() => {
        if (!isOpen || deadline === null) return;
        let timeout: ReturnType<typeof setTimeout> | undefined;
        const update = () => {
            clearTimeout(timeout);
            const currentTime = Date.now();
            setNow(currentTime);
            const remaining = Math.max(0, deadline - currentTime);
            if (remaining === 0) {
                if (notifiedDeadline.current !== deadline) {
                    notifiedDeadline.current = deadline;
                    onEnd.current?.();
                }
            } else if (document.visibilityState !== "hidden") {
                // Countdown uses ceil(minutes), so schedule the next visible change or expiry.
                timeout = setTimeout(
                    update,
                    (remaining % 60_000 || 60_000) + 1,
                );
            }
        };
        update();
        document.addEventListener("visibilitychange", update);
        window.addEventListener("focus", update);
        return () => {
            clearTimeout(timeout);
            document.removeEventListener("visibilitychange", update);
            window.removeEventListener("focus", update);
        };
    }, [isOpen, deadline]);

    const remainingMinutes =
        deadline === null
            ? null
            : Math.max(0, Math.ceil((deadline - now) / 60_000));
    return {
        remainingMinutes,
        expired: deadline !== null && deadline <= now,
    };
}
